import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { ScheduleDay } from "@/types/profile";
import { getHolidayFormClock } from "@/utils/common/holidayRange";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Extracts the time portion (HH:mm:ss) from an ISO date string, removing any timezone offset
 * @param dateString - ISO format date string (e.g., "2025-12-25T09:00:00-05:00" or "2025-12-25T09:00:00")
 * @returns The time portion as a string (e.g., "09:00:00"), or "00:00:00" if no time portion exists
 */
export const getTimePortion = (dateString: string): string => {
  const timePart = dateString.split("T")[1];
  if (!timePart) return "00:00:00";
  // Remove timezone offset if present (split by +, -, or Z)
  // Note: - must be at the end or escaped in character class to avoid being interpreted as a range
  const timeOnly = timePart.split(/[+Z-]/)[0];
  return timeOnly || "00:00:00";
};

/**
 * Cleans a schedule by filtering out invalid hours within each day and days with no valid hours
 * @param schedule - The schedule to clean
 * @returns A new schedule array with only valid days that have at least one valid hour entry
 */
export const cleanSchedule = (schedule: ScheduleDay[]): ScheduleDay[] => {
  return schedule
    .map((day) => ({
      ...day,
      hours: day.hours.filter(
        (h) => h.start !== "" && h.end !== ""
      ),
    }))
    .filter((day) => day.hours.length > 0);
};

/**
 * Compares two schedules to determine if they match perfectly
 * Schedules are considered matching if they have the same days with the same hours
 * after cleaning (removing empty/invalid entries)
 * @param schedule1 - First schedule to compare
 * @param schedule2 - Second schedule to compare
 * @returns true if the schedules match, false otherwise
 */
export const schedulesMatch = (
  schedule1: ScheduleDay[],
  schedule2: ScheduleDay[]
): boolean => {
  const cleaned1 = cleanSchedule(schedule1);
  const cleaned2 = cleanSchedule(schedule2);

  if (cleaned1.length !== cleaned2.length) {
    return false;
  }

  // Sort by day for consistent comparison
  const sorted1 = [...cleaned1].sort((a, b) => a.day.localeCompare(b.day));
  const sorted2 = [...cleaned2].sort((a, b) => a.day.localeCompare(b.day));

  for (let i = 0; i < sorted1.length; i++) {
    const day1 = sorted1[i];
    const day2 = sorted2[i];

    if (day1.day !== day2.day) {
      return false;
    }

    if (day1.hours.length !== day2.hours.length) {
      return false;
    }

    // Sort hours for consistent comparison
    const hours1 = [...day1.hours].sort((a, b) =>
      a.start.localeCompare(b.start)
    );
    const hours2 = [...day2.hours].sort((a, b) =>
      a.start.localeCompare(b.start)
    );

    for (let j = 0; j < hours1.length; j++) {
      if (
        hours1[j].start !== hours2[j].start ||
        hours1[j].end !== hours2[j].end
      ) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Formats a date string with a timezone offset without converting the time
 * Parses the date/time as if it's already in the target timezone and appends the timezone offset
 * This preserves the exact time values, just adds the offset
 * @param dateStr - Date string in ISO format (e.g., "2025-12-25T09:00:00" or "2025-12-25T09:00:00-05:00")
 * @param timezone - Target timezone (e.g., "America/New_York", "Europe/London")
 * @returns Date string with timezone offset appended (e.g., "2025-12-25T09:00:00-05:00")
 */
export const formatWithTimezone = (dateStr: string, timezone: string): string => {
  if (!dateStr) {
    return dateStr;
  }

  // Extract the date part
  const datePart = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;

  // Use the utility function to extract time portion, which handles edge cases
  let timePart = getTimePortion(dateStr);

  // Ensure time part has seconds (getTimePortion might return HH:mm)
  const timeParts = timePart.split(":");
  if (timeParts.length === 2) {
    timePart = `${timePart}:00`;
  } else if (timeParts.length === 1) {
    timePart = `${timePart}:00:00`;
  }

  // Ensure we have a valid time format
  if (!timePart.match(/^\d{2}:\d{2}:\d{2}$/)) {
    timePart = "00:00:00";
  }

  try {
    // Parse the date/time as if it's already in the target timezone
    // This creates a dayjs object representing that exact date/time in that timezone
    const dateInTz = dayjs.tz(
      `${datePart} ${timePart}`,
      "YYYY-MM-DD HH:mm:ss",
      timezone
    );

    // Check if parsing was successful
    if (!dateInTz.isValid()) {
      console.error(
        "Invalid date string:",
        dateStr,
        "parsed as:",
        `${datePart} ${timePart}`
      );
      return dateStr; // Return original if parsing fails
    }

    // Get the timezone offset for this date
    const offset = dateInTz.format("Z");

    // Return the original date/time with the timezone offset appended
    // This preserves the exact time values, just adds the offset
    return `${datePart}T${timePart}${offset}`;
  } catch (error) {
    console.error("Error formatting date with timezone:", error, dateStr);
    return dateStr; // Return original if there's an error
  }
};

type HolidaySaveInput = {
  reason: string;
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  scriptId?: string | null;
};

const toCalendarDate = (iso: string): string => `${iso.split("T")[0]}T00:00:00`;

/**
 * Builds the Galactus availability payload.
 * Dates are timezone-naive calendar days in the org timezone.
 * Partial days send `HH:mm` local times. All-day omits times so Galactus covers the inclusive dates.
 */
export const toUnavailabilityPeriodPayload = (
  holiday: HolidaySaveInput,
  allDay: boolean
) => {
  const clock = getHolidayFormClock(holiday);
  return {
    startDate: toCalendarDate(holiday.startDate),
    endDate: toCalendarDate(holiday.endDate),
    reason: holiday.reason,
    ...(allDay || clock.allDay
      ? {}
      : {
          startTime: clock.startTime,
          endTime: clock.endTime,
        }),
    scriptId: holiday.scriptId || null,
  };
};

const MINUTES_PER_DAY = 24 * 60;

export const getHolidayDurationParts = (
  startDate: string,
  endDate: string,
  allDay: boolean
): { days: number; hours: number } | null => {
  if (!startDate || !endDate) return null;

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return null;
  }

  if (allDay) {
    const days = end.startOf("day").diff(start.startOf("day"), "day") + 1;
    if (days <= 0) return null;
    return { days, hours: 0 };
  }

  const minutes = end.diff(start, "minute");
  if (minutes <= 0) return null;

  const days = Math.floor(minutes / MINUTES_PER_DAY);
  const hours = Math.floor((minutes % MINUTES_PER_DAY) / 60);
  if (days === 0 && hours === 0) {
    return { days: 0, hours: 1 };
  }

  return { days, hours };
};

export const holidayApiErrorMessage = (data: unknown, fallback: string): string => {
  // Galactus may return a bare list of problems, e.g.
  // [{ message: "Unavailability period overlaps an existing holiday", ... }]
  if (Array.isArray(data)) {
    const messages = data
      .map((entry) => {
        if (!entry || typeof entry !== "object" || !("message" in entry)) return undefined;
        return entry.message;
      })
      .filter((message): message is string => typeof message === "string" && message.length > 0);
    return messages.length > 0 ? messages.join("\n") : fallback;
  }

  if (!data || typeof data !== "object") {
    return fallback;
  }

  const body = data as {
    message?: unknown;
    detail?: unknown;
    title?: unknown;
    errors?: unknown;
  };

  if (Array.isArray(body.errors)) {
    const messages = body.errors
      .map((error) => {
        if (typeof error === "string") {
          return error;
        }
        if (error && typeof error === "object" && "message" in error) {
          const message = (error as { message?: unknown }).message;
          return typeof message === "string" ? message : undefined;
        }
        return undefined;
      })
      .filter((message): message is string => Boolean(message));
    if (messages.length > 0) {
      return messages.join("\n");
    }
  }

  if (typeof body.detail === "string" && body.detail.length > 0) {
    return body.detail;
  }
  if (typeof body.message === "string" && body.message.length > 0) {
    return body.message;
  }
  if (typeof body.title === "string" && body.title.length > 0) {
    return body.title;
  }

  return fallback;
};

